import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, AlertCircle, UserPlus, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_jci-luxury-portal/artifacts/914ad0rv_Artboard%201.png";

export default function AdminLoginPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Club JCI Ltd - Admin Login';
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await login(username, password);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, { username, password });
      
      if (response.data.token) {
        // First user - auto approved as super_admin
        await login(username, password);
        navigate('/admin');
      } else {
        // Pending approval
        setSignUpSuccess(true);
        toast.success('Registration submitted! Awaiting approval from super admin.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (signUpSuccess) {
    return (
      <div 
        data-testid="signup-success-page" 
        className="min-h-screen flex items-center justify-center p-4 marble-bg"
      >
        <div className="absolute inset-0 bg-[#051025]/80"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="glass-card p-8 rounded-sm text-center">
            <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <UserPlus className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl text-[#F1F5F9] mb-4">Registration Submitted</h2>
            <p className="text-[#94A3B8] mb-6">
              Your account is pending approval. A super admin will review and approve your access shortly.
            </p>
            <button
              onClick={() => { setSignUpSuccess(false); setIsSignUp(false); setUsername(''); setPassword(''); setConfirmPassword(''); }}
              className="btn-gold text-sm py-3 px-6 rounded-sm"
            >
              Back to Login
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div 
      data-testid="admin-login-page" 
      className="min-h-screen flex items-center justify-center p-4 marble-bg"
    >
      <div className="absolute inset-0 bg-[#051025]/80"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-card p-8 rounded-sm">
          <div className="text-center mb-8">
            <img
              src={LOGO_URL}
              alt="Club JCI Ltd"
              className="h-16 w-auto mx-auto mb-6"
            />
            <h1 className="text-2xl text-[#F1F5F9]">Admin Panel</h1>
            <p className="text-[#94A3B8] text-sm mt-2">
              {isSignUp ? 'Create an account to request access' : 'Sign in to manage your website'}
            </p>
          </div>

          {/* Toggle Tabs */}
          <div className="flex mb-6 bg-[#172A45] rounded-sm p-1">
            <button
              onClick={() => { setIsSignUp(false); setError(''); }}
              className={`flex-1 py-2 text-sm rounded-sm transition-all flex items-center justify-center gap-2 ${
                !isSignUp ? 'bg-[#D4AF37] text-[#051025]' : 'text-[#94A3B8] hover:text-[#F1F5F9]'
              }`}
            >
              <LogIn size={16} />
              Sign In
            </button>
            <button
              onClick={() => { setIsSignUp(true); setError(''); }}
              className={`flex-1 py-2 text-sm rounded-sm transition-all flex items-center justify-center gap-2 ${
                isSignUp ? 'bg-[#D4AF37] text-[#051025]' : 'text-[#94A3B8] hover:text-[#F1F5F9]'
              }`}
            >
              <UserPlus size={16} />
              Sign Up
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-sm mb-6">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {isSignUp ? (
            /* Sign Up Form */
            <form onSubmit={handleSignUp} data-testid="signup-form">
              <div className="space-y-5">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <User size={14} />
                    Username
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    data-testid="signup-username"
                    className="w-full rounded-sm"
                    placeholder="Choose a username"
                  />
                </div>

                <div>
                  <label className="form-label flex items-center gap-2">
                    <Lock size={14} />
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="signup-password"
                    className="w-full rounded-sm"
                    placeholder="Create a password"
                  />
                </div>

                <div>
                  <label className="form-label flex items-center gap-2">
                    <Lock size={14} />
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="signup-confirm-password"
                    className="w-full rounded-sm"
                    placeholder="Confirm your password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="signup-submit-btn"
                  className="w-full btn-gold-filled py-3 rounded-sm text-sm disabled:opacity-50"
                >
                  {loading ? 'Creating Account...' : 'Request Access'}
                </button>
              </div>
            </form>
          ) : (
            /* Login Form */
            <form onSubmit={handleLogin} data-testid="login-form">
              <div className="space-y-5">
                <div>
                  <label className="form-label flex items-center gap-2">
                    <User size={14} />
                    Username
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    data-testid="input-username"
                    className="w-full rounded-sm"
                    placeholder="Enter username"
                  />
                </div>

                <div>
                  <label className="form-label flex items-center gap-2">
                    <Lock size={14} />
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="input-password"
                    className="w-full rounded-sm"
                    placeholder="Enter password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full btn-gold-filled py-3 rounded-sm text-sm disabled:opacity-50"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </div>
            </form>
          )}

          {isSignUp && (
            <p className="text-[#94A3B8] text-xs text-center mt-6">
              Your account will require approval from a super admin before you can access the panel.
            </p>
          )}
        </div>

        <p className="text-center text-[#94A3B8] text-xs mt-6">
          © {new Date().getFullYear()} Club JCI Ltd. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}
